import { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { SquarePen } from 'lucide-react';
import { useMutation } from '@apollo/client/react';

import { UpdateProfileInfoDocument } from 'src/graphql/actions/profile/updateProfileInfo.mutation';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogClose,
  DialogFooter,
} from '@/components/ui/dialog';
import { ClientError, getApiError } from 'src/utils/errors';

type Props = {
  name?: string;
  phone?: string;
};

const formSchema = z.object({
  name: z.string().trim().min(1, {
    message: 'Name is required',
  }),
  phone: z.string().trim().optional(),
});

const BasicDialog = ({ name, phone }: Props) => {
  const [open, setOpen] = useState(false);
  const [updateProfile, { loading }] = useMutation(UpdateProfileInfoDocument);

  const onSubmit = async (dataForm: z.infer<typeof formSchema>) => {
    try {
      const { data } = await updateProfile({
        variables: dataForm,
      });

      if (data?.updateProfileInfo?.success) {
        toast.success('Profile updated!');
        setOpen(false);
      } else {
        throw new ClientError(data?.updateProfileInfo?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: {
      name: name || '',
      phone: phone || '',
    },
    mode: 'onChange',
  });

  const onOpenChange = (open: boolean) => {
    setOpen(open);
    if (!open) form.reset();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <SquarePen />
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] gap-6">
        <DialogHeader className="gap-1.5">
          <DialogTitle className="text-base font-bold">Update your basic information</DialogTitle>
          <DialogDescription className="text-xs">Review and update your personal details below.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="sm:mb-4">
                  <FormLabel>Full name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem className="mb-6">
                  <FormLabel>
                    Phone number <i>(optional)</i>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="sm:justify-between">
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button type="submit" disabled={!form.formState.isValid || !form.formState.isDirty} isLoading={loading}>
                Save changes
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default BasicDialog;
